import { Button } from './ui/button';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useProfile } from '../hooks/useProfile';
import {
  Goal,
  Dumbbell,
  UserIcon,
  HelpCircleIcon,
  Maximize2,
  Minimize2,
  LogOut,
  ShieldCheck,
  Home,
  Flag,
} from 'lucide-react';
import { cn } from '../lib/utils';
import VersionLabel from './VersionLabel';
import raceTrainerLogo from '../assets/racetrainer.png';

const ROUTES_PRIMARY = [
  {
    title: 'Dashboard',
    path: '/dashboard',
    icon: <Home className="h-5 w-5" />,
  },
  {
    title: 'Plan',
    path: '/plan',
    icon: <Goal className="h-5 w-5" />,
  },
  {
    title: 'Activities',
    path: '/workouts',
    icon: <Dumbbell className="h-5 w-5" />,
  },
  {
    title: 'Races',
    path: '/races',
    icon: <Flag className="h-5 w-5" />,
  },
];

const ROUTES_ADMIN = [
  {
    title: 'Admin',
    path: '/admin',
    icon: <ShieldCheck className="h-5 w-5" />,
  },
];

const ROUTES_SECONDARY = [
  {
    title: 'Settings',
    path: '/settings',
    icon: <UserIcon className="h-5 w-5" />,
  },
  {
    title: 'Support',
    path: '/support',
    icon: <HelpCircleIcon className="h-5 w-5" />,
  },
  {
    title: 'Logout',
    path: '/logout',
    icon: <LogOut className="h-5 w-5" />,
  },
];

const Sidebar = () => {
  const profile = useProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    if (isMobile) {
      return false;
    }
    const storedValue = localStorage.getItem('sidebarOpen');
    return storedValue == null ? true : JSON.parse(storedValue);
  });
  const [sideBarMinimized, setSideBarMinimized] = useState<boolean>(() => {
    if (isMobile) {
      return false;
    }
    const storedValue = localStorage.getItem('sideBarMinimized');
    return storedValue == null ? false : JSON.parse(storedValue);
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebarOpen', JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  useEffect(() => {
    localStorage.setItem('sideBarMinimized', JSON.stringify(sideBarMinimized));
  }, [sideBarMinimized]);

  useEffect(() => {
    const handleSidebarToggle = (event: CustomEvent) => {
      setSidebarOpen(event.detail.isOpen);
    };

    window.addEventListener(
      'sidebarStateChange',
      handleSidebarToggle as EventListener,
    );
    return () =>
      window.removeEventListener(
        'sidebarStateChange',
        handleSidebarToggle as EventListener,
      );
  }, []);

  const sidebarWidthClass = sideBarMinimized ? 'w-[4.5rem]' : 'w-60';
  const sideBarClasses = cn(
    'z-[60] hidden shrink-0 flex-col overflow-hidden border-r border-white/10 bg-brand-midnight p-3 shadow-2xl md:flex',
    // Desktop styles
    'h-full border-r transition-all duration-300',
    sidebarWidthClass,
  );

  return (
    <>
      <aside className={sideBarClasses}>
        <div className="flex flex-col justify-between h-full">
          <div className="flex flex-col">
            <button
              type="button"
              onClick={() => navigate({ to: '/dashboard' })}
              className={cn(
                'mb-5 flex h-14 w-full items-center transition-all duration-300',
                sideBarMinimized ? 'justify-center' : 'justify-end px-2',
              )}
              aria-label="RaceTrainer dashboard"
              title="RaceTrainer"
            >
              <img
                src={raceTrainerLogo}
                alt=""
                className={cn(
                  'object-contain object-right brightness-0 invert transition-all duration-300',
                  sideBarMinimized ? 'h-8 w-12' : 'h-12 w-36',
                )}
              />
            </button>
            {!sideBarMinimized ? (
              <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white/35">
                Training workspace
              </p>
            ) : null}
            {ROUTES_PRIMARY.map(({ title, path, icon }, index) => {
              const isActive =
                location.pathname === path ||
                location.pathname.startsWith(`${path}/`);
              return (
                <Button
                  key={index}
                  title={title}
                  variant="ghost"
                  size={!isMobile && sideBarMinimized ? 'sm' : 'lg'}
                  className={cn(
                    !isMobile ? 'transition-all duration-300' : null,
                    'relative flex my-1 text-left whitespace-nowrap justify-start w-full rounded-xl text-white/60 hover:bg-white/10 hover:text-white',
                    isActive
                      ? 'bg-white/10 text-brand-volt before:absolute before:-left-3 before:top-2 before:bottom-2 before:w-1 before:rounded-r-full before:bg-brand-volt'
                      : null,
                  )}
                  onClick={() => {
                    navigate({
                      to: path,
                    });
                    // Close mobile sidebar when nav item is clicked
                    if (isMobile) {
                      setSidebarOpen(false);
                      const event = new CustomEvent('sidebarStateChange', {
                        detail: { isOpen: false },
                      });
                      window.dispatchEvent(event);
                    }
                  }}
                >
                  <span
                    className={cn('mr-2', isActive ? 'text-brand-volt' : null)}
                  >
                    {icon}
                  </span>
                  {isMobile || !sideBarMinimized ? <span>{title}</span> : null}
                </Button>
              );
            })}
          </div>
          <div className="flex flex-col">
            {profile.data ? (
              <div className="flex flex-col py-2 border-t border-white/10 my-2">
                <button
                  type="button"
                  onClick={() => navigate({ to: '/settings' })}
                  className={cn(
                    'mb-2 flex w-full items-center gap-2 rounded-xl p-2 text-left transition-colors hover:bg-white/10',
                    sideBarMinimized ? 'justify-center' : null,
                  )}
                  title="Open profile settings"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-brand-volt text-xs font-black text-brand-midnight">
                    {profile.data.user.picture ? (
                      <img
                        src={profile.data.user.picture}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      (profile.data.user.name || 'R').charAt(0).toUpperCase()
                    )}
                  </span>
                  {!sideBarMinimized ? (
                    <span className="min-w-0">
                      <span className="block truncate text-xs font-bold text-white">
                        {profile.data.user.name || 'Runner'}
                      </span>
                      <span className="mt-0.5 block text-[10px] text-white/45">
                        Athlete profile
                      </span>
                    </span>
                  ) : null}
                </button>
                {/* Admin routes */}
                {profile.data.user.isAdmin ? (
                  <div className="flex flex-col">
                    {ROUTES_ADMIN.map(({ title, path, icon }, index) => {
                      const isActive =
                        location.pathname === path ||
                        location.pathname.startsWith(`${path}/`);
                      return (
                        <Button
                          key={index}
                          title={title}
                          variant="ghost"
                          size={!isMobile && sideBarMinimized ? 'sm' : 'lg'}
                          className={cn(
                            !isMobile ? 'transition-all duration-300' : null,
                            'relative flex my-1 text-left whitespace-nowrap justify-start w-full rounded-xl text-white/60 hover:bg-white/10 hover:text-white',
                            isActive
                              ? 'bg-white/10 text-brand-volt before:absolute before:-left-3 before:top-2 before:bottom-2 before:w-1 before:rounded-r-full before:bg-brand-volt'
                              : null,
                          )}
                          onClick={() => {
                            navigate({
                              to: path,
                            });
                          }}
                        >
                          <span
                            className={cn(
                              'mr-2',
                              isActive ? 'text-brand-volt' : null,
                            )}
                          >
                            {icon}
                          </span>
                          {isMobile || !sideBarMinimized ? (
                            <span>{title}</span>
                          ) : null}
                        </Button>
                      );
                    })}
                  </div>
                ) : null}
                {/* Secondary routes */}
                {ROUTES_SECONDARY.map(({ title, path, icon }, index) => {
                  const isActive =
                    location.pathname === path ||
                    location.pathname.startsWith(`${path}/`);
                  return (
                    <Button
                      key={index}
                      title={title}
                      variant="ghost"
                      size={!isMobile && sideBarMinimized ? 'sm' : 'lg'}
                      className={cn(
                        !isMobile ? 'transition-all duration-300' : null,
                        'relative flex my-1 text-left whitespace-nowrap justify-start w-full rounded-xl text-white/60 hover:bg-white/10 hover:text-white',
                        isActive
                          ? 'bg-white/10 text-brand-volt before:absolute before:-left-3 before:top-2 before:bottom-2 before:w-1 before:rounded-r-full before:bg-brand-volt'
                          : null,
                      )}
                      onClick={() => {
                        navigate({
                          to: path,
                        });
                        // Close mobile sidebar when nav item is clicked
                        if (isMobile) {
                          setSidebarOpen(false);
                          const event = new CustomEvent('sidebarStateChange', {
                            detail: { isOpen: false },
                          });
                          window.dispatchEvent(event);
                        }
                      }}
                    >
                      <span
                        className={cn(
                          'mr-2',
                          isActive ? 'text-brand-volt' : null,
                        )}
                      >
                        {icon}
                      </span>
                      {isMobile || !sideBarMinimized ? (
                        <span>{title}</span>
                      ) : null}
                    </Button>
                  );
                })}
              </div>
            ) : null}
            <div className="hidden md:flex border-t border-white/10 justify-between items-center pt-4">
              <Button
                title={sideBarMinimized ? 'Expand sidebar' : 'Collapse sidebar'}
                variant="ghost"
                size="icon"
                className="text-white/70 hover:bg-white/5 hover:text-white"
                onClick={() => {
                  setSideBarMinimized(!sideBarMinimized);
                }}
              >
                {sideBarMinimized ? (
                  <Maximize2 className="h-4 w-4" />
                ) : (
                  <Minimize2 className="h-4 w-4" />
                )}
              </Button>
              {!sideBarMinimized ? (
                <VersionLabel className="text-white/45" />
              ) : null}
            </div>
            {/* Mobile version */}
            <div className="flex flex-col md:hidden">
              <VersionLabel className="text-white/45" />
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
